import { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Wrench } from "lucide-react";
import { MONTH_NAMES, DAY_LABELS, getDayEntry, scheduleType, isSameDay, toMidnight } from "../utils/calendarHelpers";

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-3 h-3 rounded-sm ${color} inline-block`} />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
  );
}

export function BookingCalendar({ schedule, onDayClick, onAddBooking, onAddMaintenance }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  }

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const monthStart = new Date(year, month, 1).getTime();
  const monthEnd = new Date(year, month + 1, 0, 23, 59, 59).getTime();
  const monthEntries = schedule.filter(e => {
    const s = toMidnight(e.start_date).getTime();
    const en = toMidnight(e.end_date).getTime();
    return s <= monthEnd && en >= monthStart;
  });
  const bookingCount = monthEntries.filter(e => scheduleType(e) === "booking").length;
  const maintCount = monthEntries.filter(e => scheduleType(e) === "maintenance").length;

  return (
    <div className="hx-card hx-booking-calendar overflow-hidden border border-slate-200/80 shadow-sm">
      <div className="hx-booking-calendar-header flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 bg-white">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays size={18} className="text-blue-600" />
            <h3 className="font-display font-semibold text-slate-950">Booking Calendar</h3>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {bookingCount} booking{bookingCount !== 1 ? "s" : ""} · {maintCount} maintenance window{maintCount !== 1 ? "s" : ""}
          </div>
        </div>

        <div className="hx-booking-calendar-nav flex flex-wrap items-center gap-2">
          <button onClick={prevMonth} className="w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors">
            <ChevronLeft size={16} />
          </button>
          <span className="font-semibold text-slate-950 min-w-[168px] text-center text-sm">
            {MONTH_NAMES[month]} {year}
          </span>
          <button onClick={nextMonth} className="w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors">
            <ChevronRight size={16} />
          </button>
          <button
            onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}
            className="hx-booking-today text-xs font-semibold text-blue-700 px-3 py-2 rounded-lg border border-blue-100 bg-blue-50 hover:bg-red-100 transition-colors"
          >
            Today
          </button>
        </div>

        <div className="flex items-center gap-2">
          {onAddBooking && (
            <button onClick={onAddBooking} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition-colors">
              <Plus size={12} strokeWidth={2.5} /> Booking
            </button>
          )}
          {onAddMaintenance && (
            <button onClick={onAddMaintenance} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold shadow-sm transition-colors">
              <Wrench size={12} strokeWidth={2} /> Maintenance
            </button>
          )}
        </div>
      </div>

      <div className="hx-booking-calendar-body p-5 bg-slate-50/60">
        <div className="hx-booking-weekdays grid grid-cols-7 gap-2 mb-2">
          {DAY_LABELS.map(d => (
            <div key={d} className="text-center text-[10px] font-bold uppercase tracking-widest text-slate-400 py-1">{d}</div>
          ))}
        </div>

        <div className="hx-booking-grid grid grid-cols-7 gap-2">
          {cells.map((date, idx) => {
            if (!date) return <div key={`blank-${idx}`} className="min-h-[72px] rounded-xl" />;

            const entry = getDayEntry(date, schedule);
            const isToday = isSameDay(date, today);
            const isPast = date < today;

            let prevInSame = false;
            let nextInSame = false;
            if (entry) {
              const prev = new Date(date); prev.setDate(prev.getDate() - 1);
              const next = new Date(date); next.setDate(next.getDate() + 1);
              prevInSame = getDayEntry(prev, schedule)?.id === entry.id;
              nextInSame = getDayEntry(next, schedule)?.id === entry.id;
            }

            const isStart = entry && !prevInSame;
            const isEnd = entry && !nextInSame;
            const isSingle = isStart && isEnd;
            const type = scheduleType(entry);
            const entryTone = type === "booking"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-blue-200 bg-blue-50 text-blue-800";
            const showEntryLabel = entry && (isStart || isSingle);

            const tileClass = entry
              ? `${entryTone} shadow-sm`
              : isToday
                ? "border-blue-200 bg-blue-50 text-red-900 shadow-sm"
                : isPast
                  ? "border-slate-100 bg-white/70 text-slate-400"
                  : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50/60";

            return (
              <button
                type="button"
                key={date.toISOString()}
                className={`hx-booking-day min-h-[72px] rounded-xl border p-2 text-left transition-all hover:-translate-y-px hover:shadow-md ${tileClass}`}
                onClick={() => onDayClick(date, entry || null)}
                title={entry ? `${type === "booking" ? "Booked" : "Maintenance"}${isEnd ? " ends" : ""}` : "Available"}
                aria-label={`${date.getDate()} ${MONTH_NAMES[month]} ${year}: ${entry ? type === "booking" ? "Booked" : "Maintenance" : "Available"}`}
              >
                <div className="flex items-start justify-between gap-1">
                  <span className="text-sm font-bold leading-none">{date.getDate()}</span>
                  {isToday && (
                    <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">Today</span>
                  )}
                </div>
                <div className="mt-3 min-h-[22px]">
                  {showEntryLabel ? (
                    <span className={`hx-booking-state-chip inline-flex max-w-full rounded-full px-2 py-0.5 text-[10px] font-bold ${type === "booking" ? "bg-emerald-600 text-white" : "bg-blue-600 text-white"}`}>
                      {type === "booking" ? "B" : "M"}
                    </span>
                  ) : entry ? (
                    <span className={`hx-booking-continuation ${type === "booking" ? "bg-emerald-500" : "bg-blue-500"}`} aria-hidden="true" />
                  ) : (
                    <span className="hx-booking-available-dot" aria-hidden="true" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="hx-booking-legend flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-slate-200/70">
          <LegendItem color="bg-emerald-500" label="Booked" />
          <LegendItem color="bg-blue-500" label="Maintenance" />
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" />
            <span className="text-xs text-blue-600">Available</span>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" />
            <span className="text-xs text-slate-500">Today</span>
          </div>
        </div>
      </div>
    </div>
  );
}
